import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../hooks";
import {
  skipOnboarding,
  saveOnboarding,
  fetchOnboarding,
  selectOnboardingForRole,
  selectOnboardingStatus,
  ONBOARDING_STEPS,
} from "../../slices/onboardingSlice";
import OnboardingStepper from "./components/OnboardingStepper";
import { getStepComponentsForRole } from "./config";
import { useOnboardingRole } from "./hooks/useOnboardingRole";

const WELCOME_MESSAGES: Record<string, { title: string; subtitle: string }> = {
  Admin: {
    title: "Welcome to Storepilot",
    subtitle: "Let's get your business set up in a few simple steps.",
  },
  Manager: {
    title: "Welcome, Manager!",
    subtitle: "Let's get you familiar with inventory and your team.",
  },
  Cashier: {
    title: "Welcome, Cashier!",
    subtitle: "Let's walk you through making sales and managing customers.",
  },
};

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const role = useOnboardingRole();
  const onboarding = useAppSelector(selectOnboardingForRole(role));
  const status = useAppSelector(selectOnboardingStatus);

  const steps = ONBOARDING_STEPS[role];
  const stepComponents = getStepComponentsForRole(role);
  const welcome = WELCOME_MESSAGES[role];

  useEffect(() => {
    dispatch(fetchOnboarding(role));
  }, [role, dispatch]);

  // Already completed onboarding — go to dashboard
  if (onboarding.isComplete) {
    navigate(`/${role.toLowerCase()}`, { replace: true });
    return null;
  }

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  const StepComponent = stepComponents[onboarding.currentStep];

  const handleSkip = () => {
    dispatch(skipOnboarding({ role }));
    dispatch(
      saveOnboarding({
        role,
        current_step: onboarding.currentStep,
        completed_steps: [...onboarding.completedSteps],
        is_complete: false,
        is_skipped: true,
      })
    );
    navigate(`/${role.toLowerCase()}`);
  };

  // For Admin, step 0 (plan selection) is compulsory so hide skip
  const canSkip = role === "Admin" ? onboarding.currentStep > 0 : true;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-800">{welcome.title}</h1>
          <p className="text-gray-500 mt-1">{welcome.subtitle}</p>
        </div>

        {/* Stepper */}
        <OnboardingStepper
          steps={steps}
          currentStep={onboarding.currentStep}
          completedSteps={[...onboarding.completedSteps]}
        />

        {/* Step content */}
        <div className="mt-8">
          <StepComponent />
        </div>

        {/* Skip button */}
        {canSkip && (
          <div className="mt-8 text-center">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-400 hover:text-gray-600 underline"
            >
              Skip Onboarding
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
